import React, { useState, useEffect, useCallback } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { Tab } from "@headlessui/react";
import {
  UsersIcon,
  UserGroupIcon,
  BookOpenIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

function AdminDashboard({ user, userType }) {
  const [activeTab, setActiveTab] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    lessonsAccessed: 0,
    logins: 0,
  });
  const [loading, setLoading] = useState(true);

  // Reporting state
  const [reportingStats, setReportingStats] = useState({
    lessonsAccessed: 0,
    logins: 0,
    newUsers: 0,
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportingLoading, setReportingLoading] = useState(false);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!user) return;

      try {
        // Get user's organization info
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          let orgId;

          // Handle both document reference and string ID formats
          if (
            userData.organization_id &&
            typeof userData.organization_id === "object" &&
            userData.organization_id.id
          ) {
            orgId = userData.organization_id.id;
          } else if (userData.organization_id) {
            orgId = userData.organization_id;
          }

          if (orgId) {
            // Get organization details
            const orgRef = doc(db, "organizations", orgId);
            const orgSnap = await getDoc(orgRef);

            if (orgSnap.exists()) {
              setOrganization({ id: orgSnap.id, ...orgSnap.data() });

              // Fetch stats
              await fetchStats(orgId);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching organization data:", error);
        toast.error("Error loading organization data");
        setLoading(false);
      }
    };

    fetchOrganizationData();
  }, [user]);

  // Fetch reporting data for date range
  const fetchReportingData = useCallback(async () => {
    if (!startDate || !endDate || !organization) return;

    setReportingLoading(true);
    try {
      const orgRef = doc(db, "organizations", organization.id);
      const startTimestamp = new Date(startDate);
      const endTimestamp = new Date(endDate);
      endTimestamp.setHours(23, 59, 59, 999); // End of day

      // Get all activity for organization and filter in memory to avoid index issues
      const activityRef = collection(db, "user_activity");
      const activityQuery = query(
        activityRef,
        where("organization_id", "==", orgRef)
      );
      const activitySnapshot = await getDocs(activityQuery);

      // Filter and count in memory
      let lessonsAccessed = 0;
      let logins = 0;
      const monthlyGroups = {};

      activitySnapshot.forEach((doc) => {
        const data = doc.data();
        const activityDate = data.created_date.toDate();

        // Check if activity is within date range
        if (activityDate >= startTimestamp && activityDate <= endTimestamp) {
          if (data.type === "accessed_lesson") {
            lessonsAccessed++;

            // Group by month for chart - use local month to avoid timezone issues
            const monthKey = `${activityDate.getFullYear()}-${String(
              activityDate.getMonth() + 1
            ).padStart(2, "0")}`;

            monthlyGroups[monthKey] = (monthlyGroups[monthKey] || 0) + 1;
          } else if (data.type === "user_login") {
            logins++;
          }
        }
      });

      // Get new users created in date range
      const usersRef = collection(db, "users");
      const usersQuery = query(
        usersRef,
        where("organization_id", "==", orgRef)
      );
      const usersSnapshot = await getDocs(usersQuery);

      let newUsers = 0;
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.created_time) {
          const userCreatedDate = new Date(userData.created_time);
          if (
            userCreatedDate >= startTimestamp &&
            userCreatedDate <= endTimestamp
          ) {
            newUsers++;
          }
        }
      });

      // Convert monthly data to array and sort
      const monthlyArray = Object.entries(monthlyGroups)
        .map(([month, count]) => {
          // Parse the month key directly to avoid timezone issues
          const [year, monthNum] = month.split("-");
          const monthIndex = parseInt(monthNum) - 1; // Convert back to 0-indexed
          const date = new Date(parseInt(year), monthIndex, 1);
          const label = date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });

          return {
            month,
            count,
            label,
          };
        })
        .sort((a, b) => a.month.localeCompare(b.month));

      setReportingStats({
        lessonsAccessed,
        logins,
        newUsers,
      });
      setMonthlyData(monthlyArray);
    } catch (error) {
      console.error("Error fetching reporting data:", error);
      toast.error("Error loading reporting data");
    } finally {
      setReportingLoading(false);
    }
  }, [startDate, endDate, organization]);

  // Fetch reporting data when dates change
  useEffect(() => {
    if (startDate && endDate && organization) {
      fetchReportingData();
    }
  }, [startDate, endDate, organization, fetchReportingData]);

  const fetchStats = async (orgId) => {
    try {
      // Get total users in organization
      const usersRef = collection(db, "users");
      const orgRef = doc(db, "organizations", orgId);
      const usersQuery = query(
        usersRef,
        where("organization_id", "==", orgRef)
      );
      const usersSnapshot = await getCountFromServer(usersQuery);

      // Get total admins in organization
      const adminsQuery = query(
        usersRef,
        where("organization_id", "==", orgRef),
        where("user_type", "==", "admin")
      );
      const adminsSnapshot = await getCountFromServer(adminsQuery);

      // Get total logins for organization
      const activityRef = collection(db, "user_activity");
      const loginsQuery = query(
        activityRef,
        where("organization_id", "==", orgRef),
        where("type", "==", "user_login")
      );
      const loginsSnapshot = await getCountFromServer(loginsQuery);

      // Get total lesson accesses for organization
      const lessonAccessQuery = query(
        activityRef,
        where("organization_id", "==", orgRef),
        where("type", "==", "accessed_lesson")
      );
      const lessonAccessSnapshot = await getCountFromServer(lessonAccessQuery);

      setStats({
        totalUsers: usersSnapshot.data().count,
        totalAdmins: adminsSnapshot.data().count,
        lessonsAccessed: lessonAccessSnapshot.data().count,
        logins: loginsSnapshot.data().count,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Link copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading admin dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Organization not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userInviteUrl = `https://app.kuzkohistory.com/sign-up?id=${organization.id}`;
  // const adminInviteUrl = `https://app.kuzkohistory.com/sign-up-admin?signUpCode=${organization.admin_signup_code}`;

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-2">{organization.name}</p>
          </div>

          {/* Stats Section */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Organization Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UsersIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Users
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalUsers}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Admins
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalAdmins}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BookOpenIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Lessons Accessed
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.lessonsAccessed}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <KeyIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Logins
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.logins}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
            <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                    selected
                      ? "bg-white text-primary-700 shadow"
                      : "text-gray-700 hover:text-primary-600 hover:bg-white/[0.12]"
                  }`
                }
              >
                General
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                    selected
                      ? "bg-white text-primary-700 shadow"
                      : "text-gray-700 hover:text-primary-600 hover:bg-white/[0.12]"
                  }`
                }
              >
                Reporting
              </Tab>
            </Tab.List>

            <Tab.Panels>
              {/* General Tab */}
              <Tab.Panel>
                <div className="space-y-6">
                  {/* Organization Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Organization Information
                    </h3>
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Organization Name
                          </label>
                          <p className="text-gray-900">{organization.name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              organization.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {organization.active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Invite URLs */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Invite URLs
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          User Invite URL
                        </label>
                        <div className="flex">
                          <input
                            type="text"
                            value={userInviteUrl}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                          />
                          <button
                            onClick={() => copyToClipboard(userInviteUrl)}
                            className="px-4 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            <span>Copy</span>
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Share this link with users to invite them to your
                          organization
                        </p>
                      </div>

                      {/*<div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Admin Invite URL
                        </label>
                        <div className="flex">
                          <input
                            type="text"
                            value={adminInviteUrl}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                          />
                          <button
                            onClick={() => copyToClipboard(adminInviteUrl)}
                            className="px-4 py-2 bg-primary-700 text-white rounded-r-md hover:bg-primary-800 transition-colors flex items-center space-x-2"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            <span>Copy</span>
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Share this link with other admins to invite them to
                          your organization
                        </p>
                      </div>*/}
                    </div>
                  </div>
                </div>
              </Tab.Panel>

              {/* Reporting Tab */}
              <Tab.Panel>
                <div className="space-y-6">
                  {/* Date Range Picker */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Date Range
                    </h3>
                    <div className="flex gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reporting Stats */}
                  {startDate && endDate && (
                    <>
                      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Period Totals
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              {reportingLoading
                                ? "..."
                                : reportingStats.lessonsAccessed}
                            </div>
                            <div className="text-sm text-gray-600">
                              Lessons Accessed
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              {reportingLoading ? "..." : reportingStats.logins}
                            </div>
                            <div className="text-sm text-gray-600">Logins</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">
                              {reportingLoading
                                ? "..."
                                : reportingStats.newUsers}
                            </div>
                            <div className="text-sm text-gray-600">
                              New Users
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Monthly Chart */}
                      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Lessons Accessed by Month
                        </h3>
                        {reportingLoading ? (
                          <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                            <p className="mt-2 text-gray-600">
                              Loading chart data...
                            </p>
                          </div>
                        ) : monthlyData.length > 0 ? (
                          <div className="flex items-end justify-between space-x-2 h-64">
                            {monthlyData.map((item) => {
                              const maxCount = Math.max(
                                ...monthlyData.map((d) => d.count)
                              );
                              const barHeight =
                                maxCount > 0
                                  ? Math.max((item.count / maxCount) * 100, 5)
                                  : 5;

                              return (
                                <div
                                  key={item.month}
                                  className="flex flex-col items-center flex-1"
                                >
                                  <div className="text-xs font-medium text-gray-900 mb-2 text-center">
                                    {item.count}
                                  </div>
                                  <div
                                    className="w-full bg-gray-200 rounded-t relative"
                                    style={{ height: "200px" }}
                                  >
                                    <div
                                      className="bg-primary-600 w-full rounded-t transition-all duration-300 absolute bottom-0"
                                      style={{
                                        height: `${barHeight}%`,
                                        minHeight: "8px",
                                      }}
                                    ></div>
                                  </div>
                                  <div className="text-xs text-gray-600 mt-2 text-center">
                                    {item.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No lesson access data available for this period.
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Placeholder when no dates selected */}
                  {(!startDate || !endDate) && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                      <div className="text-center py-8">
                        <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          Select Date Range
                        </h3>
                        <p className="text-gray-600">
                          Choose start and end dates to view detailed reporting
                          and analytics.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
